// A shop is registered for PAN only or for VAT (tenant_settings.tax_registration).
// It decides what a bill is called and how the shop's number is labelled; the
// lines still carry whatever VAT their products have.

export type TaxRegistration = 'pan' | 'vat'

type HasRegistration = { tax_registration?: TaxRegistration | null } | null | undefined

/** PAN only unless the shop says it is VAT registered. */
export function registrationOf(settings: HasRegistration): TaxRegistration {
  return settings?.tax_registration === 'vat' ? 'vat' : 'pan'
}

/** Only a VAT-registered shop issues a "Tax invoice". */
export function billTitle(
  registration: TaxRegistration,
  status: 'draft' | 'finalized' | 'cancelled',
): string {
  const name = registration === 'vat' ? 'Tax invoice' : 'Invoice'
  if (status === 'draft')
    return `Draft (not ${registration === 'vat' ? 'a tax invoice' : 'an invoice'})`
  return status === 'cancelled' ? `${name} — CANCELLED` : name
}

/** The label of the shop's own registration number. */
export function taxNumberLabel(registration: TaxRegistration): string {
  return registration === 'vat' ? 'VAT No.' : 'PAN'
}

/**
 * Whether a bill shows its VAT rows. A PAN shop's bill without VAT leaves them
 * out; one that did charge VAT still shows it, so the bill stays truthful.
 */
export function showsVat(registration: TaxRegistration, taxPaisa: number): boolean {
  return registration === 'vat' || taxPaisa !== 0
}

/**
 * The small VAT note under a bill line on screen: "VAT 13%", or "Exempt" for a
 * VAT shop's line without VAT. A PAN shop's line without VAT says nothing
 * ("exempt" is a VAT term).
 */
export function lineVatLabel(registration: TaxRegistration, rateBp: number): string | null {
  if (rateBp) return `VAT ${rateBp / 100}%`
  return registration === 'vat' ? 'Exempt' : null
}
